/** Token storage in sessionStorage and refresh timing. Storage can be unavailable; every access is guarded. */

export interface TokenSet {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  /** Epoch milliseconds when the access token expires. */
  expiresAt: number;
}

export const TOKEN_KEY = 'rm.tokens';
export const PKCE_KEY = 'rm.pkce';
export const RETURN_KEY = 'rm.returnTo';

export function storageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
export function storageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked: tokens live only in memory for this page */
  }
}
export function storageRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function saveTokens(tokens: TokenSet): void {
  storageSet(TOKEN_KEY, JSON.stringify(tokens));
}

export function loadTokens(): TokenSet | undefined {
  const raw = storageGet(TOKEN_KEY);
  if (!raw) return undefined;
  try {
    const t = JSON.parse(raw) as Partial<TokenSet>;
    if (typeof t.accessToken !== 'string' || typeof t.expiresAt !== 'number') return undefined;
    return t as TokenSet;
  } catch {
    return undefined;
  }
}

export function clearTokens(): void {
  storageRemove(TOKEN_KEY);
}

/** Cognito token endpoint response. */
export interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type?: string;
}

/** Builds a TokenSet. A refresh response has no refresh token, so the previous one is kept. */
export function toTokenSet(res: TokenResponse, now: number, previous?: TokenSet): TokenSet {
  const refreshToken = res.refresh_token ?? previous?.refreshToken;
  return {
    accessToken: res.access_token,
    ...(res.id_token ? { idToken: res.id_token } : previous?.idToken ? { idToken: previous.idToken } : {}),
    ...(refreshToken ? { refreshToken } : {}),
    expiresAt: now + res.expires_in * 1000,
  };
}

/** Refresh this long before expiry. */
export const REFRESH_SKEW_MS = 60_000;

/** Milliseconds from `now` until the tokens should be refreshed (never negative). */
export function refreshDelay(tokens: Pick<TokenSet, 'expiresAt'>, now: number, skewMs = REFRESH_SKEW_MS): number {
  return Math.max(0, tokens.expiresAt - skewMs - now);
}

export function isExpired(tokens: Pick<TokenSet, 'expiresAt'>, now: number): boolean {
  return tokens.expiresAt <= now;
}

/**
 * Schedules `refresh` before the tokens expire. Returns a cancel function.
 * Without a refresh token nothing is scheduled.
 */
export function scheduleRefresh(
  tokens: TokenSet,
  refresh: () => void,
  now: number = Date.now(),
  // Wrapped on purpose: browsers throw "Illegal invocation" when setTimeout is called as a method of another object.
  timers: { set: (fn: () => void, ms: number) => unknown; clear: (id: unknown) => void } = {
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  },
): () => void {
  if (!tokens.refreshToken) return () => {};
  const id = timers.set(refresh, refreshDelay(tokens, now));
  return () => timers.clear(id);
}
